import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
  const { t } = useTranslation();
  const QUICK_ACTIONS = [
    { label: t('chatWidget.whatIs') },
    { label: t('chatWidget.pricing') },
    { label: t('chatWidget.howToStart') },
  ];
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `sales-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ role: 'assistant', content: t('chatWidget.welcome') }]);
    }
  }, [isOpen]);

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { branch_id: 'sales', session_id: sessionId, message: msg });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: t('chatWidget.error') }]);
    } finally { setLoading(false); }
  };

  if (!isOpen) return (
    <button onClick={() => setIsOpen(true)} data-testid="landing-chat-toggle"
      style={{ position: 'fixed', bottom: 24, left: 24, zIndex: 50, width: 52, height: 52, borderRadius: '50%',
        background: 'linear-gradient(135deg, #D97706, #F59E0B)', color: '#fff', border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 24px rgba(217,119,6,0.4)',
        transition: 'transform 0.2s', animation: 'chatPulse 2s ease infinite' }}
      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
      <MessageCircle style={{ width: 22, height: 22 }} />
      <style>{`@keyframes chatPulse{0%,100%{box-shadow:0 4px 24px rgba(217,119,6,0.4)}50%{box-shadow:0 4px 32px rgba(217,119,6,0.6)}}`}</style>
    </button>
  );

  return (
    <div data-testid="landing-chat-panel" style={{ position: 'fixed', bottom: 24, left: 24, zIndex: 50, width: 360, maxWidth: 'calc(100vw - 32px)', height: 480, maxHeight: 'calc(100vh - 100px)',
      background: '#0A0A0F', borderRadius: 20, boxShadow: '0 12px 48px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column',
      overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
      {/* Header */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'linear-gradient(135deg, rgba(217,119,6,0.1), transparent)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 12, background: 'linear-gradient(135deg, #D97706, #F59E0B)',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sparkles style={{ width: 18, height: 18, color: '#fff' }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#F0F0F5', fontFamily: 'Tajawal, sans-serif' }}>{t('chatWidget.title')}</div>
            <div style={{ fontSize: 10, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#10B981' }} /> {t('chatWidget.online')}
            </div>
          </div>
        </div>
        <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 4 }}>
          <X style={{ width: 18, height: 18 }} />
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: 10 }} dir="rtl">
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-start' : 'flex-end', gap: 6 }}>
            <div style={{
              maxWidth: '85%', padding: '10px 14px', fontSize: 13, lineHeight: 1.8, fontFamily: 'Tajawal, sans-serif',
              borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              background: m.role === 'user' ? 'linear-gradient(135deg, #D97706, #F59E0B)' : 'rgba(255,255,255,0.06)',
              color: m.role === 'user' ? '#fff' : '#F0F0F5',
              border: m.role === 'user' ? 'none' : '1px solid rgba(255,255,255,0.08)',
              whiteSpace: 'pre-line'
            }}>{m.content}</div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <div style={{ padding: '10px 16px', borderRadius: '16px 16px 16px 4px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Loader2 style={{ width: 16, height: 16, color: '#D97706', animation: 'spin 1s linear infinite' }} />
            </div>
          </div>
        )}
        {messages.length <= 1 && !loading && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {QUICK_ACTIONS.map((qa, i) => (
              <button key={i} onClick={() => send(qa.label)} style={{
                padding: '6px 14px', borderRadius: 16, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: 'rgba(217,119,6,0.1)', border: '1px solid rgba(217,119,6,0.3)', color: '#D97706',
                fontFamily: 'Tajawal, sans-serif', transition: 'all 0.2s'
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(217,119,6,0.2)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(217,119,6,0.1)'; }}
              >{qa.label}</button>
            ))}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '10px 14px', borderTop: '1px solid rgba(255,255,255,0.08)' }} dir="rtl">
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') send(); }}
            placeholder={t('chatWidget.placeholder')} disabled={loading}
            style={{ flex: 1, height: 38, borderRadius: 19, border: '1px solid rgba(255,255,255,0.08)',
              padding: '0 16px', fontSize: 13, fontFamily: 'Tajawal, sans-serif',
              background: 'rgba(255,255,255,0.04)', color: '#F0F0F5', outline: 'none' }}
            data-testid="landing-chat-input"
          />
          <button onClick={() => send()} disabled={!input.trim() || loading} data-testid="landing-chat-send"
            style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg, #D97706, #F59E0B)',
              color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: (!input.trim() || loading) ? 0.4 : 1 }}>
            <Send style={{ width: 16, height: 16 }} />
          </button>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
